import React from "react";

import { useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Home, Package, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Simple animation variants for page elements
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 260,
        damping: 20,
      },
    },
  };

  // Floating animation for the package icon
  const floatingPackageVariants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        ease: "easeInOut" as const,
        repeat: Infinity,
        repeatType: "reverse" as const,
      },
    },
  };

  // Handle go to dashboard
  const handleGoToDashboard = () => {
    navigate({ to: "/" });
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[85vh] p-4 sm:p-6">
      <motion.div className="w-full max-w-md" variants={containerVariants} initial="hidden" animate="visible">
        {/* Main 404 display */}
        <motion.div variants={itemVariants} className="mb-8 text-center">
          <div className="inline-block p-6 mb-4 bg-primary/5 rounded-full">
            <motion.div variants={floatingPackageVariants} animate="animate">
              <Package className="h-16 w-16 text-primary/70" />
            </motion.div>
          </div>
          <h1 className="text-8xl font-bold text-primary/30 tracking-tighter">404</h1>
          <div className="mt-4 text-xl font-medium text-foreground">{t("errors.not_found.title", "Package Not Found")}</div>
        </motion.div>

        {/* Simple message card */}
        <motion.div variants={itemVariants}>
          <Card className="border-muted/40 bg-card/80 shadow-xs">
            <CardContent className="pt-6 pb-4 text-center">
              <p className="text-muted-foreground">
                {t("errors.not_found.message", "Looks like this package got lost in our warehouse. Our robots are still searching, but you might want to try a different route.")}
              </p>

              {/* Small playful detail - barcode */}
              <div className="my-6 mx-auto w-48 h-12 relative">
                <div className="flex h-full items-end justify-center space-x-1">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-muted-foreground/70"
                      style={{
                        height: `${Math.random() * 70 + 30}%`,
                        width: "2px",
                      }}
                    />
                  ))}
                </div>
                <div className="mt-2 text-xs text-muted-foreground/70 font-mono tracking-wider text-center">404-NOT-FOUND</div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col sm:flex-row justify-center gap-3 pb-6">
              <Button variant="outline" onClick={() => globalThis.history.back()} className="w-full sm:w-auto gap-2">
                <RotateCcw className="h-4 w-4" />
                {t("errors.not_found.go_back", "Go Back")}
              </Button>

              <Button onClick={handleGoToDashboard} className="w-full sm:w-auto gap-2">
                <Home className="h-4 w-4" />
                {t("errors.not_found.back_to_dashboard", "Go to Dashboard")}
              </Button>
            </CardFooter>
          </Card>
        </motion.div>

        {/* Small attribution/hint */}
        <motion.div variants={itemVariants} className="mt-4 text-center">
          <p className="text-xs text-muted-foreground/60 flex items-center justify-center gap-1">
            <span aria-label="Robot">{"\u{1F916}"}</span>
            <span>Warehouse Operations</span>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default NotFound;
